import { profileDisplayName } from "@/stores/documents/repository/mappers";
import type {
  CommentRow,
  DocumentComment,
  DocumentThread,
  DocumentThreadActionType,
  DocumentThreadActivity,
  ThreadActivityRow,
  ThreadRow,
} from "./types";
import { DOCUMENT_THREAD_ACTION_TYPES } from "./types";

export function mapComment(row: CommentRow, currentUserId: number | null): DocumentComment {
  const mentions = row.mentions ?? [];
  return {
    id: row.id,
    threadId: row.thread_id,
    body: row.body,
    authorId: row.author_id,
    author: row.author
      ? {
          id: row.author.id,
          first_name: row.author.first_name,
          last_name: row.author.last_name,
          email: row.author.email,
          avatar_url: row.author.avatar_url,
          color: row.author.color,
        }
      : null,
    createdAt: row.created_at,
    modifiedAt: row.modified_at,
    mentionedUserIds: mentions.map((m) => m.mentioned_user_id),
    unreadForMe:
      currentUserId != null &&
      mentions.some((m) => m.mentioned_user_id === currentUserId && m.read_at === null),
  };
}

/**
 * A thread with no live root is a data anomaly (the root insert failed after
 * the thread insert and the rollback did not land); it is dropped rather than
 * rendered as an empty card.
 */
export function mapThread(row: ThreadRow, currentUserId: number | null): DocumentThread | null {
  const comments = (row.comments ?? []).filter((c) => c.deleted_at === null);
  const rootRow = comments.find((c) => c.is_root);
  if (!rootRow) return null;
  const issue = row.linked_issue && row.linked_issue.deleted_at === null ? row.linked_issue : null;
  return {
    id: row.id,
    documentId: row.document_id,
    documentVersion: row.document?.version ?? null,
    documentFilename: row.document?.filename ?? null,
    buildingId: row.building_id,
    projectId: row.project_id,
    pageNumber: row.page_number,
    createdAt: row.created_at,
    createdById: row.created_by,
    createdByName: profileDisplayName(row.created_by_profile),
    resolvedAt: row.resolved_at,
    resolvedById: row.resolved_by,
    resolvedByName: profileDisplayName(row.resolved_by_profile),
    linkedIssue: issue
      ? {
          id: issue.id,
          number: issue.issue_number,
          title: issue.title,
          status: issue.status,
          projectId: issue.building_project_id,
        }
      : null,
    commentSource: row.comment_source ?? "document",
    root: mapComment(rootRow, currentUserId),
    replies: comments.filter((c) => !c.is_root).map((c) => mapComment(c, currentUserId)),
  };
}

export function mapThreads(rows: ThreadRow[], currentUserId: number | null): DocumentThread[] {
  const out: DocumentThread[] = [];
  for (const row of rows) {
    const thread = mapThread(row, currentUserId);
    if (thread) out.push(thread);
  }
  return out;
}

/**
 * Panel order: threads on the document as a whole first, then by page, then
 * oldest first — a reader walks the file top to bottom.
 */
export function sortThreads(threads: DocumentThread[]): DocumentThread[] {
  return [...threads].sort((a, b) => {
    const pa = a.pageNumber ?? 0;
    const pb = b.pageNumber ?? 0;
    if (pa !== pb) return pa - pb;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

function isActionType(value: string): value is DocumentThreadActionType {
  return (DOCUMENT_THREAD_ACTION_TYPES as readonly string[]).includes(value);
}

/** Unknown action types (a newer schema) are dropped rather than rendered blank. */
export function mapActivity(rows: ThreadActivityRow[]): DocumentThreadActivity[] {
  const out: DocumentThreadActivity[] = [];
  for (const row of rows) {
    if (!isActionType(row.action_type)) continue;
    const metadata = row.metadata ?? {};
    const actorName = metadata.actor_name;
    out.push({
      id: row.id,
      threadId: row.thread_id,
      actionType: row.action_type,
      actorId: row.actor_id,
      actorName: typeof actorName === "string" && actorName.trim() ? actorName : null,
      createdAt: row.created_at,
      metadata,
    });
  }
  return out;
}

/** The first line of a comment, trimmed to `max` characters, for an issue title. */
export function firstLine(body: string, max = 80): string {
  const line =
    body
      .split(/\r?\n/)
      .find((l) => l.trim().length > 0)
      ?.trim() ?? "";
  if (line.length <= max) return line;
  return `${line.slice(0, max - 1).trimEnd()}…`;
}
